import * as React from 'react';

import cn from '@/utils/cn';

type CardPadding = 'none' | 'sm' | 'md' | 'lg';

// The responsive step is carried by a CSS custom property, never by a `sm:`
// padding utility. twMerge can only dedupe classes that share a modifier, so a
// base `sm:p-6` survives a caller's unprefixed `p-0` and wins at >=640px. A
// single unprefixed `p-[var(--card-p)]` conflicts with `p-0` on the same key,
// so the caller wins at every width and the leftover `[--card-p:*]` classes are
// inert. Values mirror the utilities they replace (p-3/4/5 -> sm:p-4/6/8).
const paddingStyles: Record<CardPadding, string> = {
  none: '',
  sm: 'p-[var(--card-p)] [--card-p:0.75rem] sm:[--card-p:1rem]',
  md: 'p-[var(--card-p)] [--card-p:1rem] sm:[--card-p:1.5rem]',
  lg: 'p-[var(--card-p)] [--card-p:1.25rem] sm:[--card-p:2rem]',
};

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  // Defaults to `md` (matches the pre-merge app Card). Use `none` when composing
  // with CardHeader/CardContent (which bring their own padding).
  padding?: CardPadding;
}

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, padding = 'md', ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'rounded-xl border bg-card text-card-foreground shadow',
        paddingStyles[padding],
        className,
      )}
      {...props}
    />
  ),
);
Card.displayName = 'Card';

export const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        // See paddingStyles above for why the sm: step is a custom property.
        'flex flex-col space-y-1.5 p-[var(--card-header-p)] [--card-header-p:1rem] sm:[--card-header-p:1.5rem]',
        className,
      )}
      {...props}
    />
  ),
);
CardHeader.displayName = 'CardHeader';

export const CardTitle = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('font-semibold leading-none tracking-tight', className)}
      {...props}
    />
  ),
);
CardTitle.displayName = 'CardTitle';

export const CardDescription = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />
));
CardDescription.displayName = 'CardDescription';

export const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        // See paddingStyles above for why the sm: step is a custom property.
        'p-[var(--card-content-p)] pt-0 [--card-content-p:1rem] sm:[--card-content-p:1.5rem]',
        className,
      )}
      {...props}
    />
  ),
);
CardContent.displayName = 'CardContent';

export const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        // See paddingStyles above for why the sm: step is a custom property.
        'flex flex-wrap items-center gap-2 p-[var(--card-footer-p)] pt-0 [--card-footer-p:1rem] sm:[--card-footer-p:1.5rem]',
        className,
      )}
      {...props}
    />
  ),
);
CardFooter.displayName = 'CardFooter';
